import { Controller } from "@hotwired/stimulus"
import { ArcadeStage } from "../utils/arcade_stage"

// Le jonglage : votre pastille devient un ballon. Chaque clic la relance, la gravité fait le
// reste, et elle tombe de plus en plus vite.
//
// Ce contrôleur est posé sur la pastille elle-même — dans le menu du compte et sur sa propre
// fiche — à la place de la modale de photo de profil, tant que le mode fun est ouvert. Il
// est aussi posé sur une borne de la salle d'arcade, où il n'y a aucune pastille dans le
// DOM : c'est pourquoi le ballon est DÉCRIT par le serveur (photo, initiales, couleur — cf.
// ArcadeHelper#arcade_avatar_payload) plutôt que cloné depuis la page.
const GRAVITY_START = 900        // px/s²
const GRAVITY_STEP = 26          // par point marqué
const GRAVITY_MAX = 2200
const KICK = -620                // impulsion verticale, px/s
const SIDE_KICK = 260            // dispersion horizontale d'un coup de pied
const BALL_SIZE = 96

export default class extends Controller {
  static values = { labels: Object, scoresUrl: String, avatar: Object }

  start(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
    })
    this.stage.open()
    this.#buildBall()
    this.#play()
  }

  #buildBall() {
    this.field = document.createElement("div")
    this.field.className = "arcade-juggle"

    this.ball = document.createElement("button")
    this.ball.type = "button"
    this.ball.className = "arcade-juggle__ball"
    this.ball.setAttribute("aria-label", this.labelsValue.title || "")
    this.ball.appendChild(this.#face())
    this.ball.addEventListener("pointerdown", (event) => {
      event.preventDefault()
      this.#kick()
    })

    this.field.appendChild(this.ball)
    this.stage.board.appendChild(this.field)
  }

  // Les initiales sont posées d'emblée : elles sont à la fois le cas « pas de photo » et le
  // filet quand la photo n'arrive jamais. L'image vient par-dessus une fois chargée, ce qui
  // évite aussi de voir une pastille vide le temps du téléchargement.
  #face() {
    const { url, initials, background } = this.avatarValue || {}

    const face = document.createElement("span")
    face.className = "arcade-juggle__face"
    face.textContent = initials || "?"
    if (background) face.style.backgroundColor = background

    if (!url) return face

    const image = new Image()
    image.alt = ""
    image.className = "arcade-juggle__photo"
    image.addEventListener("load", () => face.classList.add("arcade-juggle__face--photo"), { once: true })
    image.addEventListener("error", () => image.remove(), { once: true })
    face.appendChild(image)
    image.src = url

    return face
  }

  #play() {
    const box = this.field.getBoundingClientRect()
    this.width = box.width
    this.height = box.height
    this.x = this.width / 2 - BALL_SIZE / 2
    this.y = this.height * 0.3
    this.vx = 0
    this.vy = 0
    this.gravity = GRAVITY_START
    this.over = false
    this.started = false
    this.ball.classList.remove("arcade-juggle__ball--dropped")
    this.stage.setScore(0)
    this.stage.setHint(this.labelsValue.start || "")
    this.#position()
  }

  #stop() {
    if (this.frame) window.cancelAnimationFrame(this.frame)
    this.frame = null
    this.field = null
  }

  #kick() {
    if (this.over) return

    if (!this.started) {
      this.started = true
      this.stage.setHint(this.labelsValue.hint || "")
      this.last = null
      this.frame = window.requestAnimationFrame((time) => this.#loop(time))
    }

    this.stage.setScore(this.stage.score + 1)
    this.gravity = Math.min(GRAVITY_MAX, this.gravity + GRAVITY_STEP)
    this.vy = KICK
    // Le ballon part un peu de travers, sinon il retombe indéfiniment au même endroit et il
    // n'y a plus rien à jouer.
    this.vx += (Math.random() - 0.5) * SIDE_KICK
  }

  #loop(time) {
    if (this.over || !this.field) return

    // Première frame : pas d'écart de temps connu, donc pas de pas d'intégration.
    // Et on borne le pas — un onglet revenu au premier plan après trente secondes
    // téléporterait le ballon à travers le sol.
    const delta = this.last == null ? 0 : Math.min((time - this.last) / 1000, 0.05)
    this.last = time

    this.vy += this.gravity * delta
    this.x += this.vx * delta
    this.y += this.vy * delta

    if (this.x <= 0 || this.x + BALL_SIZE >= this.width) {
      this.vx *= -0.85
      this.x = Math.max(0, Math.min(this.x, this.width - BALL_SIZE))
    }
    if (this.y <= 0) {
      this.y = 0
      this.vy = Math.abs(this.vy) * 0.6
    }

    if (this.y + BALL_SIZE >= this.height) return this.#drop()

    this.#position()
    this.frame = window.requestAnimationFrame((next) => this.#loop(next))
  }

  #position() {
    this.ball.style.transform = `translate3d(${this.x}px, ${this.y}px, 0)`
  }

  #drop() {
    this.over = true
    if (this.frame) window.cancelAnimationFrame(this.frame)
    this.y = this.height - BALL_SIZE
    this.#position()
    this.ball.classList.add("arcade-juggle__ball--dropped")
    this.stage.finish({ score: this.stage.score, message: this.labelsValue.dropped || "" })
  }
}
